import { RouterAddress } from "../../constants/RouterAddress";

export type AppTourStep = {
  id: string;
  title: string;
  description: string;
  selector: string;
  route?: string;
  routeIncludes?: string[];
  actionRequired?: boolean;
  nextLabel?: string;
};

export type TourId = "ncp_automation" | "reminders" | "website" | "nurse_next_steps" | "nurse_assessment" | "nurse_delegation";

export type TutorialOption = {
  id: TourId;
  title: string;
  description: string;
  icon: string;
};

export const providerTutorialOptions: TutorialOption[] = [
  {
    id: "ncp_automation",
    title: "Assessment -> Negotiated Care Plan Automation",
    description: "Create a home, add a resident, and launch the assessment-to-care-plan workflow.",
    icon: "pi pi-sparkles",
  },
  // {
  //   id: "reminders",
  //   title: "Add Reminders",
  //   description: "Open the reminders workspace and create a recurring reminder with schedule details.",
  //   icon: "pi pi-bell",
  // },
  {
    id: "website",
    title: "Create AFH Website",
    description: "Set up a home, open the home workspace, edit the website, review templates, and publish when ready.",
    icon: "pi pi-globe",
  },
];

export const nurseTutorialOptions: TutorialOption[] = [
  {
    id: "nurse_assessment",
    title: "New Assessment and Care Plan",
    description: "Start from a resident on the nurse dashboard and open the assessment and care plan workflow.",
    icon: "pi pi-file-edit",
  },
  {
    id: "nurse_delegation",
    title: "Delegation Forms",
    description: "Start from a resident on the nurse dashboard and work through delegation forms.",
    icon: "pi pi-sitemap",
  },
];

export const tutorialOptions: TutorialOption[] = providerTutorialOptions;

export const tourStepsById: Record<TourId, AppTourStep[]> = {
  ncp_automation: [
    {
      id: "add-home-cta",
      title: "Start by adding a home",
      description: "Click Add Home to create the first AFH profile. This is the base workspace for residents, documents, reminders, and website setup.",
      selector: '[data-tour="provider-add-home"]',
      route: RouterAddress.PROVIDER_HOME,
      actionRequired: true,
    },
    {
      id: "save-home",
      title: "Save the new home",
      description: "Complete the home form and save it. After the home is created, the dashboard will unlock the next step.",
      selector: '[data-tour="provider-save-home"]',
      route: RouterAddress.PROVIDER_HOME,
      actionRequired: true,
    },
    {
      id: "add-resident-cta",
      title: "Add your first resident",
      description: "Use this shortcut on the home card to add a resident directly from the provider dashboard.",
      selector: '[data-tour="provider-add-resident"]',
      route: RouterAddress.PROVIDER_HOME,
      actionRequired: true,
    },
    {
      id: "save-resident",
      title: "Save the resident",
      description: "Enter the resident name and save. After that, the Resident Added dialog will guide you to the next care-planning step.",
      selector: '[data-tour="provider-save-resident"]',
      route: RouterAddress.PROVIDER_HOME,
      actionRequired: true,
    },
    {
      id: "resident-next-step",
      title: "Start with the assessment",
      description: "Use the Assessment to Care Plan automation dialog to jump straight into the assessment upload step and begin the care plan workflow.",
      selector: '[data-tour="provider-next-step-upload-assessment"]',
      route: RouterAddress.PROVIDER_HOME,
      actionRequired: true,
    },
    {
      id: "ncp-overview",
      title: "Assessment to Care Plan overview",
      description: "This workspace turns the latest resident assessment into a Care Plan draft, then helps you continue into the related planning sections.",
      selector: '[data-tour="resident-upload-assessment"]',
      routeIncludes: ["/resident/", "/ncp"],
      actionRequired: false,
    },
    {
      id: "choose-assessment-file",
      title: "Choose the assessment file",
      description: "Start by selecting the PDF, DOC, or DOCX assessment file you want to use for care plan automation.",
      selector: '[data-tour="resident-upload-assessment-choose"]',
      routeIncludes: ["/resident/", "/ncp"],
      actionRequired: false,
    },
    {
      id: "upload-assessment",
      title: "Upload the assessment",
      description: "After choosing the file, click upload to save it into the binder and start the AI-assisted care plan import.",
      selector: '[data-tour="resident-upload-assessment-submit"]',
      routeIncludes: ["/resident/", "/ncp"],
      actionRequired: false,
    },
    {
      id: "ncp-care-plan-sections",
      title: "Review care plan sections",
      description: "Once the draft is ready, the Care Plan sections below let you review, edit, and save the final resident plan step by step.",
      selector: '[data-tour="resident-careplan-sections"]',
      routeIncludes: ["/resident/", "/ncp"],
      actionRequired: false,
      nextLabel: "Finish",
    },
  ],
  reminders: [
    {
      id: "reminder-open-manage",
      title: "Open Manage",
      description: "Use the Manage menu in the header to access reminders, documents, and other provider tools from anywhere in the app.",
      selector: ".app-header-manage-tour-target > .p-menuitem-link",
      actionRequired: false,
    },
    {
      id: "reminder-open-from-header",
      title: "Open reminders from the header",
      description: "Use Header -> Manage -> Reminders to open the reminder workspace from anywhere in the app.",
      selector: ".app-header-reminder-tour-target > .p-menuitem-link",
      actionRequired: true,
    },
    {
      id: "reminder-overview",
      title: "Reminder workspace overview",
      description: "This page keeps your active reminders, templates, upcoming items, and reminder history in one place.",
      selector: '[data-tour="reminder-overview"]',
      route: RouterAddress.USER_REMINDER_PAGE,
      actionRequired: false,
    },
    {
      id: "reminder-actions",
      title: "Reminder quick actions",
      description: "Use these actions to create a new reminder, review upcoming reminders, or open history without leaving the page.",
      selector: '[data-tour="reminder-actions"]',
      route: RouterAddress.USER_REMINDER_PAGE,
      actionRequired: false,
    },
    {
      id: "reminder-templates",
      title: "Template library",
      description: "These AFH reminder templates help you start faster with common recurring workflows and compliance tasks.",
      selector: '[data-tour="reminder-templates"]',
      route: RouterAddress.USER_REMINDER_PAGE,
      actionRequired: false,
    },
    {
      id: "reminder-new-cta",
      title: "Open the reminder form",
      description: "This New Reminder button launches the custom reminder editor where you set title, schedule, timing, and sample email options.",
      selector: '[data-tour="reminder-new"]',
      route: RouterAddress.USER_REMINDER_PAGE,
      actionRequired: false,
    },
    {
      id: "reminder-editor",
      title: "Reminder editor",
      description: "The reminder dialog is where you define the title, description, recurrence, early warning timing, timezone, and schedule preview.",
      selector: '[data-tour="reminder-create-dialog"]',
      route: RouterAddress.USER_REMINDER_PAGE,
      actionRequired: false,
    },
    {
      id: "reminder-save",
      title: "Create the reminder",
      description: "When you are ready, this Create button saves the reminder. The tour stops here so you can choose whether to create one now or just explore the form.",
      selector: '[data-tour="reminder-save"]',
      route: RouterAddress.USER_REMINDER_PAGE,
      actionRequired: false,
      nextLabel: "Finish",
    },
  ],
  website: [
    {
      id: "website-open-workspace",
      title: "Open the website workspace",
      description: "Start from Provider Home and use the Website button on the selected home to open the dedicated website management page.",
      selector: '[data-tour="provider-open-website"]',
      route: RouterAddress.PROVIDER_HOME,
      actionRequired: true,
    },
    {
      id: "website-overview",
      title: "Website workspace overview",
      description: "This page is your website command center. From here you can review live status, copy the public URL, open editing, manage themes, review inquiries, and publish updates.",
      selector: '[data-tour="website-overview"]',
      route: RouterAddress.MANAGE_WEBSITE,
      actionRequired: false,
    },
    {
      id: "website-status-url",
      title: "Status and public URL",
      description: "Check whether the site is live, and copy the public website link once it has been published.",
      selector: '[data-tour="website-status-url"]',
      route: RouterAddress.MANAGE_WEBSITE,
      actionRequired: false,
    },
    {
      id: "website-quick-actions",
      title: "Quick actions",
      description: "These buttons let you open the editor, publish the current draft, review inquiries, and check website analytics from one place.",
      selector: '[data-tour="website-quick-actions"]',
      route: RouterAddress.MANAGE_WEBSITE,
      actionRequired: false,
    },
    {
      id: "website-edit-content",
      title: "Edit template and content",
      description: "Use Edit Template to open the editor workspace. The tour will open it for you here so you can see where each website section is managed.",
      selector: '[data-tour="website-edit-template"]',
      route: RouterAddress.MANAGE_WEBSITE,
      actionRequired: false,
    },
    {
      id: "website-theme-picker",
      title: "Choose a website theme",
      description: "This area lets you preview templates, switch the draft theme, and decide which layout and visual style you want before publishing.",
      selector: '[data-tour="website-theme-picker"]',
      route: RouterAddress.MANAGE_WEBSITE,
      actionRequired: false,
    },
    {
      id: "website-qr-code",
      title: "Share with QR code",
      description: "Once the website is live, this card shows a QR code you can print or share so visitors can open the site quickly.",
      selector: '[data-tour="website-qr-code"]',
      route: RouterAddress.MANAGE_WEBSITE,
      actionRequired: false,
    },
    {
      id: "website-editor-timeline",
      title: "Editor timeline",
      description: "The timeline lets you jump between website sections like Header, Banner, About, Services, Gallery, and Contact while editing the draft.",
      selector: '[data-tour="website-editor-timeline"]',
      route: RouterAddress.MANAGE_WEBSITE,
      actionRequired: false,
    },
    {
      id: "website-publish",
      title: "Publish the website",
      description: "When your edits look right, use Publish Website to push the current draft live. Reset To Defaults restores the draft without changing the live site until you publish again.",
      selector: '[data-tour="website-publish"]',
      route: RouterAddress.MANAGE_WEBSITE,
      actionRequired: false,
      nextLabel: "Finish",
    },
  ],
  nurse_next_steps: [
    {
      id: "nurse-next-overview",
      title: "Choose the easiest next step",
      description: "You have 3 choices here. Use files to create a new assessment and delegation forms, create the assessment manually, or open delegation forms and fill them out by hand.",
      selector: '[data-tour="nurse-next-overview"]',
      routeIncludes: ["/resident/", "/profile"],
      actionRequired: false,
    },
    {
      id: "nurse-next-automation",
      title: "Use resident files",
      description: "This is the easiest option for most users. Add files like a MAR sheet, notes, or demographics, and AFH Hub will draft the assessment and new delegation forms.",
      selector: '[data-tour="nurse-next-automation"]',
      routeIncludes: ["/resident/", "/profile"],
      actionRequired: false,
    },
    {
      id: "nurse-next-add-files",
      title: "Step 1: Add files",
      description: "Click here to upload resident documents.",
      selector: '[data-tour="nurse-next-add-files"]',
      routeIncludes: ["/resident/", "/profile"],
      actionRequired: false,
    },
    {
      id: "nurse-next-tags",
      title: "Step 2: Tag each file",
      description: "After uploading, choose the right tag for each file, like MAR Sheet, Face Sheet, or Assessment Notes.",
      selector: '[data-tour="nurse-next-tags"]',
      routeIncludes: ["/resident/", "/profile"],
      actionRequired: false,
    },
    {
      id: "nurse-next-start",
      title: "Step 3: Start drafting",
      description: "When your files are ready, click Start Drafting. After that, you can open the assessment or the delegation forms.",
      selector: '[data-tour="nurse-next-start"]',
      routeIncludes: ["/resident/", "/profile"],
      actionRequired: false,
    },
    {
      id: "nurse-next-manual-assessment",
      title: "Create the assessment manually",
      description: "If you do not want to use files, click Create Manually to open a new assessment draft.",
      selector: '[data-tour="nurse-next-manual-assessment"]',
      routeIncludes: ["/resident/", "/profile"],
      actionRequired: false,
    },
    {
      id: "nurse-next-manual-delegation",
      title: "Create delegation forms manually",
      description: "Use this section to open delegation forms, start a new version, and fill them out by hand.",
      selector: '[data-tour="nurse-next-manual-delegation"]',
      routeIncludes: ["/resident/", "/profile"],
      actionRequired: false,
      nextLabel: "Finish",
    },
  ],
  nurse_assessment: [
    {
      id: "nurse-dashboard-add-provider-assessment",
      title: "Add an AFH provider",
      description: "Start here if you have not added an AFH provider yet. Click Add AFH Provider.",
      selector: '[data-tour="nurse-dashboard-add-provider"]',
      route: RouterAddress.NURSE_HOME,
      actionRequired: true,
    },
    {
      id: "nurse-dashboard-add-resident-assessment",
      title: "Add a resident",
      description: "After your AFH provider is ready, click Add Resident to create the first resident record.",
      selector: '[data-tour="nurse-dashboard-add-resident"]',
      route: RouterAddress.NURSE_HOME,
      actionRequired: true,
    },
    {
      id: "nurse-dashboard-assessment",
      title: "Open assessment and care plan",
      description: "Start from the nurse dashboard. Click Assessment + Care Plan for the resident you want to work on.",
      selector: '[data-tour="nurse-dashboard-assessment"]',
      route: RouterAddress.NURSE_HOME,
      actionRequired: true,
    },
    {
      id: "nurse-assessment-workflow",
      title: "Assessment and care plan workflow",
      description: "This area helps you manage the current assessment, continue into the care plan steps, start a new version, and move to delegation forms if needed.",
      selector: '[data-tour="nurse-assessment-workflow"]',
      routeIncludes: ["/nurse/resident/", "/assessment/"],
      actionRequired: false,
    },
    {
      id: "nurse-assessment-documents",
      title: "Documents",
      description: "Use these buttons when you are ready to generate or download the assessment document for this assessment + care plan workflow.",
      selector: '[data-tour="nurse-assessment-documents"]',
      routeIncludes: ["/nurse/resident/", "/assessment/"],
      actionRequired: false,
      nextLabel: "Finish",
    },
  ],
  nurse_delegation: [
    {
      id: "nurse-dashboard-add-provider-delegation",
      title: "Add an AFH provider",
      description: "Start here if you have not added an AFH provider yet. Click Add AFH Provider.",
      selector: '[data-tour="nurse-dashboard-add-provider"]',
      route: RouterAddress.NURSE_HOME,
      actionRequired: true,
    },
    {
      id: "nurse-dashboard-add-resident-delegation",
      title: "Add a resident",
      description: "After your AFH provider is ready, click Add Resident to create the first resident record.",
      selector: '[data-tour="nurse-dashboard-add-resident"]',
      route: RouterAddress.NURSE_HOME,
      actionRequired: true,
    },
    {
      id: "nurse-dashboard-delegation",
      title: "Open delegation forms",
      description: "Start from the nurse dashboard. Click Delegation for the resident you want to work on.",
      selector: '[data-tour="nurse-dashboard-delegation"]',
      route: RouterAddress.NURSE_HOME,
      actionRequired: true,
    },
    {
      id: "nurse-delegation-landing",
      title: "Delegation forms page",
      description: "This page shows the delegation form library and any saved forms for the resident.",
      selector: '[data-tour="nurse-delegation-landing"]',
      routeIncludes: ["/nurse/resident/", "/delegation/"],
      actionRequired: false,
    },
    {
      id: "nurse-delegation-create-form",
      title: "Create or open a form",
      description: "Choose a form from the library, then click Create New Form to start a version for this resident.",
      selector: '[data-tour="nurse-delegation-create-form"]',
      routeIncludes: ["/nurse/resident/", "/delegation/"],
      actionRequired: true,
    },
    {
      id: "nurse-delegation-actions",
      title: "Save, complete, or download",
      description: "Use these actions to save the draft, mark it complete, or download the form.",
      selector: '[data-tour="nurse-delegation-actions"]',
      routeIncludes: ["/nurse/resident/", "/delegation/"],
      actionRequired: false,
      nextLabel: "Finish",
    },
  ],
};
